import { useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  medicationFormSchema,
  type MedicationFormValues,
  type CreateMedicationInput,
} from "@shared/validation/medication.schema";
import { MEDICATION_FREQUENCIES, FREQUENCY_LABELS } from "@shared/constants/frequency";
import {
  useCreateMedication,
  useUpdateMedication,
  type MedicationSummary,
} from "../../lib/api/useMedications";
import { useToast } from "../../lib/toast/ToastProvider";
import { ApiError } from "../../lib/api/client";
import { INPUT_CLASS } from "../../lib/forms/inputClass";
import { DialogShell } from "../ui/DialogShell";
import { Field } from "../ui/Field";
import { Button } from "../ui/Button";

interface MedicationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  childId: string;
  medication?: MedicationSummary;
}

function toDateInputValue(iso: string | null | undefined): string {
  if (!iso) return "";
  return iso.slice(0, 10);
}

function fromDateInputValue(v: string | undefined): Date | null {
  if (!v) return null;
  // End of day UTC to avoid timezone-drift edge cases on date-only values.
  const d = new Date(`${v}T23:59:59.000Z`);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function MedicationDialog({
  open,
  onOpenChange,
  childId,
  medication,
}: MedicationDialogProps) {
  const isEdit = Boolean(medication);
  const createMutation = useCreateMedication(childId);
  const updateMutation = useUpdateMedication(childId, medication?.id ?? "");
  const activeMutation = isEdit ? updateMutation : createMutation;
  const { publish } = useToast();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<MedicationFormValues>({
    resolver: zodResolver(medicationFormSchema),
    defaultValues: {
      name: medication?.name ?? "",
      dosage: medication?.dosage ?? "",
      frequency: medication?.frequency ?? "as_needed",
      expiresAt: toDateInputValue(medication?.expiresAt ?? null),
      notes: medication?.notes ?? null,
    },
  });

  // Run the reset only on the closed→open TRANSITION, not on every render while
  // open: createMutation/updateMutation are new object identities each render and
  // mutation.reset() notifies subscribers, so an unguarded effect re-runs forever
  // and wipes anything the user types (pre-existing bug, found in the reskin QA).
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) {
      reset({
        name: medication?.name ?? "",
        dosage: medication?.dosage ?? "",
        frequency: medication?.frequency ?? "as_needed",
        expiresAt: toDateInputValue(medication?.expiresAt ?? null),
        notes: medication?.notes ?? null,
      });
      // Clear any stale error banner from a previous open/close cycle.
      createMutation.reset();
      updateMutation.reset();
    }
    wasOpen.current = open;
  }, [open, medication, reset, createMutation, updateMutation]);

  const onSubmit = async (data: MedicationFormValues) => {
    const payload: CreateMedicationInput = {
      name: data.name,
      dosage: data.dosage,
      frequency: data.frequency,
      expiresAt: fromDateInputValue(data.expiresAt),
      notes: data.notes && data.notes.length > 0 ? data.notes : null,
    };
    try {
      await activeMutation.mutateAsync(payload);
      publish({
        variant: "success",
        title: isEdit ? "Medication updated successfully" : "Medication added successfully",
      });
      onOpenChange(false);
    } catch (err) {
      publish({
        variant: "error",
        title: isEdit ? "Failed to update medication" : "Failed to add medication",
        description: err instanceof ApiError ? err.message : undefined,
      });
    }
  };

  return (
    <DialogShell
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      title={isEdit ? "Edit Medication" : "Add Medication"}
      description="Add a new medication with dosage, frequency, and special instructions."
    >
      <form onSubmit={handleSubmit(onSubmit)} className="mt-6 space-y-4">
        <Field label="Medication Name *" htmlFor="med-name" error={errors.name?.message}>
          <input
            id="med-name"
            type="text"
            {...register("name")}
            className={INPUT_CLASS}
            placeholder="e.g., Albuterol, Ibuprofen"
          />
        </Field>

        <Field label="Dosage *" htmlFor="med-dosage" error={errors.dosage?.message}>
          <input
            id="med-dosage"
            type="text"
            {...register("dosage")}
            className={INPUT_CLASS}
            placeholder="e.g., 5mg, 2 puffs, 1 tablet"
          />
        </Field>

        <Field label="Frequency *" htmlFor="med-frequency" error={errors.frequency?.message}>
          <select id="med-frequency" {...register("frequency")} className={INPUT_CLASS}>
            {MEDICATION_FREQUENCIES.map((f) => (
              <option key={f} value={f}>
                {FREQUENCY_LABELS[f]}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Expiration Date" htmlFor="med-expires" error={errors.expiresAt?.message}>
          <input
            id="med-expires"
            type="date"
            {...register("expiresAt")}
            className={INPUT_CLASS}
          />
        </Field>

        <Field label="Additional Notes" htmlFor="med-notes" error={errors.notes?.message}>
          <textarea
            id="med-notes"
            rows={2}
            {...register("notes")}
            className={INPUT_CLASS}
            placeholder="Additional instructions, side effects to watch for, etc..."
          />
        </Field>

        {activeMutation.error instanceof ApiError && (
          <p className="rounded-xl bg-red-50 p-3 text-sm text-red-700">
            {activeMutation.error.message}
          </p>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="submit" isLoading={isSubmitting || activeMutation.isPending}>
            {activeMutation.isPending ? "Saving…" : isEdit ? "Save Changes" : "Add Medication"}
          </Button>
        </div>
      </form>
    </DialogShell>
  );
}
